import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  associateCustomerUser,
  getCustomerPhoneSyncStatus,
  synchronizeCustomerPhone
} from '../../../../services/2talk/customer-phone-sync.service';
import { shouldRequestPhoneSyncStatus } from './customer-phone-sync.utils';

const errorMessage = (error, fallback) => {
  const errors = error && error.response && error.response.data && error.response.data.errors;
  return errors && errors[0] ? errors[0] : fallback;
};

const CustomerPhoneSyncCard = ({
  isOpen,
  chat,
  intl,
  updateCurrentChatCustomerData,
  setGlobalLoading,
  setNotificationMessage,
  setNotificationVariant,
  setOpenNotification
}) => {
  const [phoneStatus, setPhoneStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [saving, setSaving] = useState(false);

  const chatId = chat && chat.id;
  const messageProviderId = chat && chat.messageproviderid;
  const isWhatsapp = Number(messageProviderId) === 2;
  const activeChatId = useRef(chatId);
  activeChatId.current = chatId;

  useEffect(() => {
    let active = true;
    setPendingAction(null);

    if (!shouldRequestPhoneSyncStatus(isOpen, chatId, messageProviderId)) {
      setPhoneStatus(null);
      setLoadError(false);
      setLoading(false);
      return () => { active = false; };
    }

    setLoading(true);
    setPhoneStatus(null);
    setLoadError(false);
    getCustomerPhoneSyncStatus(chatId)
      .then(result => {
        if (!active) return;
        if (result && result.success && result.data) {
          setPhoneStatus(result.data);
        } else {
          setLoadError(true);
        }
      })
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
    // A consulta deve reagir somente à abertura da lateral ou à troca da conversa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, chatId]);

  if (!isWhatsapp) return null;

  const notify = (message, variant) => {
    if (setNotificationMessage) setNotificationMessage(message);
    if (setNotificationVariant) setNotificationVariant(variant);
    if (setOpenNotification) setOpenNotification(true);
  };

  const applyReturnedStatus = (status) => {
    setPhoneStatus(status);
    const currentPhone = chat && chat.customer ? chat.customer.phone : null;
    if (status
      && status.customerphone !== currentPhone
      && updateCurrentChatCustomerData
      && chat
      && chat.customer) {
      updateCurrentChatCustomerData(chat.id, { ...chat.customer, phone: status.customerphone || '' });
    }
  };

  const executePendingAction = () => {
    if (!pendingAction || saving) return;

    setSaving(true);
    if (setGlobalLoading) setGlobalLoading(true);
    const requestedChatId = chatId;
    const request = pendingAction === 'associate'
      ? associateCustomerUser(chat)
      : synchronizeCustomerPhone(chatId, pendingAction);

    request
      .then(result => {
        if (result && result.success && result.data) {
          if (activeChatId.current === requestedChatId) applyReturnedStatus(result.data);
          notify(intl.formatMessage({ id: 'chat.phone_sync.success' }), 'success');
          setPendingAction(null);
          return;
        }

        const message = result && result.errors && result.errors[0]
          ? result.errors[0]
          : intl.formatMessage({ id: 'chat.phone_sync.error' });
        notify(message, 'error');
      })
      .catch(error => notify(
        errorMessage(error, intl.formatMessage({ id: 'chat.phone_sync.error' })),
        'error'
      ))
      .finally(() => {
        setSaving(false);
        if (setGlobalLoading) setGlobalLoading(false);
      });
  };

  const phoneValue = value => value || intl.formatMessage({ id: 'chat.phone_sync.not_informed' });
  const confirmationTitle = pendingAction === 'associate'
    ? 'chat.phone_sync.confirm_associate_title'
    : 'chat.phone_sync.confirm_sync_title';
  const confirmationText = pendingAction === 'associate'
    ? intl.formatMessage({ id: 'chat.phone_sync.confirm_associate' })
    : pendingAction === 1
      ? intl.formatMessage(
        { id: 'chat.phone_sync.confirm_customer_to_user' },
        { source: phoneValue(phoneStatus && phoneStatus.customerphone), target: phoneValue(phoneStatus && phoneStatus.userphone) }
      )
      : intl.formatMessage(
        { id: 'chat.phone_sync.confirm_user_to_customer' },
        { source: phoneValue(phoneStatus && phoneStatus.userphone), target: phoneValue(phoneStatus && phoneStatus.customerphone) }
      );

  return (
    <>
      <Divider />
      <Box sx={{ padding: '3%', color: '#444' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, marginBottom: 1.5 }}>
          {intl.formatMessage({ id: 'chat.phone_sync.title' })}
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#777' }}>
            <CircularProgress size={18} />
            <Typography variant="body2">{intl.formatMessage({ id: 'chat.phone_sync.loading' })}</Typography>
          </Box>
        )}

        {!loading && loadError && (
          <Typography variant="body2" color="error">
            {intl.formatMessage({ id: 'chat.phone_sync.error' })}
          </Typography>
        )}

        {!loading && phoneStatus && phoneStatus.status === 'not_associated' && (
          <Box>
            <Typography variant="body2" sx={{ marginBottom: 1.5 }}>
              {intl.formatMessage({ id: 'chat.phone_sync.not_associated' })}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonAddAlt1Icon />}
              onClick={() => setPendingAction('associate')}
              sx={{ textTransform: 'none' }}
            >
              {intl.formatMessage({ id: 'chat.phone_sync.associate' })}
            </Button>
          </Box>
        )}

        {!loading && phoneStatus && phoneStatus.status === 'synchronized' && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: '#4f8a10' }}>
            <CheckCircleOutlineIcon fontSize="small" />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {intl.formatMessage({ id: 'chat.phone_sync.synchronized' })}
              </Typography>
              <Typography variant="caption">{phoneValue(phoneStatus.customerphone)}</Typography>
            </Box>
          </Box>
        )}

        {!loading && phoneStatus && phoneStatus.status === 'divergent' && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: '#9a6700', marginBottom: 1.5 }}>
              <WarningAmberIcon fontSize="small" />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: 'chat.phone_sync.divergent' })}
                </Typography>
                {phoneStatus.outboundsource === 'user' && (
                  <Typography variant="caption">
                    {intl.formatMessage({ id: 'chat.phone_sync.outbound_user' })}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ fontSize: 13, marginBottom: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 0.75 }}>
                <PhoneIcon sx={{ fontSize: 17, color: '#999' }} />
                <span><strong>{intl.formatMessage({ id: 'chat.phone_sync.customer' })}:</strong> {phoneValue(phoneStatus.customerphone)}</span>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ fontSize: 17, color: '#999' }} />
                <span><strong>{intl.formatMessage({ id: 'chat.phone_sync.user' })}:</strong> {phoneValue(phoneStatus.userphone)}</span>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {phoneStatus.cancopycustomertouser && (
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  endIcon={<ArrowForwardRoundedIcon />}
                  onClick={() => setPendingAction(1)}
                  sx={{
                    minHeight: 38,
                    justifyContent: 'space-between',
                    borderColor: '#c5dca9',
                    borderRadius: 1.5,
                    color: '#56852b',
                    backgroundColor: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: 'left',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#8dbb55',
                      backgroundColor: '#f6faef'
                    }
                  }}
                >
                  {intl.formatMessage({ id: 'chat.phone_sync.customer_to_user' })}
                </Button>
              )}
              {phoneStatus.cancopyusertocustomer && (
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  endIcon={<ArrowForwardRoundedIcon />}
                  onClick={() => setPendingAction(2)}
                  sx={{
                    minHeight: 38,
                    justifyContent: 'space-between',
                    borderColor: '#c5dca9',
                    borderRadius: 1.5,
                    color: '#56852b',
                    backgroundColor: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: 'left',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#8dbb55',
                      backgroundColor: '#f6faef'
                    }
                  }}
                >
                  {intl.formatMessage({ id: 'chat.phone_sync.user_to_customer' })}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {!loading && phoneStatus && phoneStatus.status === 'unavailable' && (
          <Typography variant="body2">
            {intl.formatMessage({ id: 'chat.phone_sync.unavailable' })}
          </Typography>
        )}
      </Box>

      <Dialog open={pendingAction !== null} onClose={() => !saving && setPendingAction(null)}>
        <DialogTitle>{intl.formatMessage({ id: confirmationTitle })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmationText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setPendingAction(null)}>
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          <Button disabled={saving} variant="contained" onClick={executePendingAction}>
            {intl.formatMessage({ id: 'chat.phone_sync.confirm_button' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerPhoneSyncCard;
